import React, { useEffect } from 'react';
import { colors } from '../styles/variables';
import CourseHeader from '../components/course/CourseHeader';
import CourseAbout from '../components/course/CourseAbout';
import CourseCurriculum from '../components/course/CourseCurriculum';
import CourseProjects from '../components/course/CourseProjects';
import CourseInstructors from '../components/course/CourseInstructors';
import CourseCta from '../components/course/CourseCta';
import CourseReviewSection from '../components/course/CourseReviewSection';
import Seo from '../components/common/Seo';
import { projectsByCourse } from '../data/projects';

const courseJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: '데이터베이스 모델링',
  description: '데이터 설계, SQL, NoSQL, 실전 DB 모델링까지 한 번에 배우는 1:1 멘토링 과정.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'ElectroWave Club',
    sameAs: 'https://electrowave.kr',
  },
  inLanguage: 'ko',
  educationalLevel: 'intermediate',
  url: 'https://electrowave.kr/database',
};

const DatabasePage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  // 코스 헤더 데이터
  const headerData = {
    tag: '데이터 관리',
    title: '데이터베이스 모델링 과정',
    description: '효율적인 데이터 설계와 관리 방법을 배우며 실제 서비스에 적용 가능한 데이터베이스 역량을 갖춥니다. 전기·정보통신 시스템에 최적화된 데이터 모델링 기법을 습득하세요.',
    duration: '총 32시간 / 8주 과정',
    level: '중급자 대상',
    bgColor: colors.dbColor,
    bgImage: '/assets/images/course-thumb-database.png'
  };
  
  // 코스 소개 데이터
  const aboutData = {
    subtitle: '데이터베이스 설계의 기초부터 고급 모델링 기법까지 체계적으로 배우는 실무 중심 과정입니다.',
    forWhom: [
      '효율적인 데이터베이스 설계 방법을 배우고 싶은 분',
      '데이터 모델링의 개념을 실무에 적용하고 싶은 분',
      'SQL 최적화 및 성능 향상 기법을 익히고 싶은 분',
      '정보통신 분야에서 데이터 설계 역량을 강화하고 싶은 분',
      'SQLD, 정보처리기사 등 자격증 취득을 준비하는 분'
    ],
    skills: [
      '데이터 모델링의 핵심 개념 이해 및 적용 능력',
      '관계형 데이터베이스 설계 및 정규화 기법',
      'SQL 쿼리 작성 및 성능 최적화 능력',
      '대용량 데이터 처리를 위한 인덱싱 전략',
      'NoSQL 데이터베이스 활용 능력'
    ],
    imageSrc: '/assets/images/course-thumb-database.png'
  };
  
  // 커리큘럼 데이터
  const curriculumData = {
    subtitle: '체계적이고 단계적인 커리큘럼으로 데이터 모델링의 기초부터 고급 기법까지 배웁니다.',
    modules: [
      {
        title: '1. 데이터베이스 기초 및 모델링 개요',
        description: '데이터베이스의 개념과 종류, 데이터 모델링의 중요성과 기본 원리를 이해합니다.',
        lessons: [
          '데이터베이스 개념과 발전 과정',
          '관계형 데이터베이스와 NoSQL 비교',
          '데이터 모델링의 정의와 중요성',
          '모델링 프로세스 및 도구 소개'
        ]
      },
      {
        title: '2. 개념적 데이터 모델링',
        description: '비즈니스 요구사항을 추상화하여 개념적 모델로 표현하는 방법을 학습합니다.',
        lessons: [
          '엔티티(Entity) 식별 및 정의',
          '속성(Attribute) 정의 및 분류',
          '관계(Relationship) 식별 및 표현',
          'ER(Entity-Relationship) 다이어그램 작성'
        ]
      },
      {
        title: '3. 논리적 데이터 모델링',
        description: '개념적 모델을 관계형 데이터베이스 구조로 변환하는 방법을 배웁니다.',
        lessons: [
          '릴레이션(테이블) 설계',
          '키(Key) 설계: 기본키, 외래키, 후보키',
          '정규화 과정과 원칙',
          '무결성 제약조건 정의'
        ]
      },
      {
        title: '4. 정규화와 역정규화',
        description: '데이터 중복을 최소화하고 데이터 일관성을 유지하기 위한 정규화 기법을 학습합니다.',
        lessons: [
          '제1정규형(1NF) ~ 제5정규형(5NF)',
          '함수적 종속성 이해',
          '성능 향상을 위한 역정규화 기법',
          '정규화와 역정규화의 균형'
        ]
      },
      {
        title: '5. 물리적 데이터 모델링',
        description: '논리적 모델을 실제 DBMS 환경에 맞게 구현하는 방법을 배웁니다.',
        lessons: [
          '테이블 및 컬럼 명명 규칙',
          '데이터 타입 선택 및 최적화',
          '인덱스 설계 및 최적화',
          '파티셔닝 전략'
        ]
      },
      {
        title: '6. SQL 작성 및 최적화',
        description: '효율적인 SQL 쿼리 작성법과 성능 최적화 기법을 학습합니다.',
        lessons: [
          'DDL, DML, DCL, TCL 이해',
          '복잡한 JOIN 및 서브쿼리 작성',
          '인덱스를 활용한 쿼리 최적화',
          '실행 계획(Execution Plan) 분석'
        ]
      },
      {
        title: '7. NoSQL 데이터베이스 모델링',
        description: '관계형 데이터베이스의 한계를 뛰어넘는 NoSQL 데이터베이스의 모델링 기법을 습득합니다.',
        lessons: [
          'NoSQL 데이터베이스 유형: Document, Key-Value, Column-Family, Graph',
          'MongoDB 스키마 설계',
          '임베딩과 레퍼런싱 전략',
          'NoSQL의 성능 최적화 기법'
        ]
      }
    ]
  };
  
  // 프로젝트 데이터
  const projectsData = {
    subtitle: '수업에서 배운 내용을 바로 적용할 수 있는 실전형 데이터베이스 설계 프로젝트를 수행합니다.',
    projects: projectsByCourse.database,
  };
  
  // 강사진 데이터
  const instructorsData = {
    subtitle: '데이터베이스 분야에서 오랜 실무 경험을 갖춘 전문가들이 여러분의 성장을 돕습니다.',
    instructors: [
      {
        name: 'Rechard Park',
        title: '데이터 아키텍트 / DBA',
        bio: '15년 이상의 데이터베이스 설계 및 관리 경력을 보유한 전문가입니다. 대규모 전력 관리 시스템, 통신 인프라 모니터링 시스템 등 다양한 프로젝트에서 데이터 아키텍처를 담당했습니다.',
        image: '/assets/images/instructor-park-jiwon.png'
      }
    ]
  };
  
  // CTA 데이터
  const ctaData = {
    title: '데이터베이스 전문가로 성장하세요',
    subtitle: 'ElectroWave Club의 데이터베이스 모델링 과정을 통해 효율적인 데이터 설계와 관리 역량을 갖춘 전문가로 성장하세요.',
    bgColor: colors.dbColor,
    endColor: '#4A148C'  // 진한 보라색
  };
  
  return (
    <>
      <Seo
        title="데이터베이스 모델링"
        description="데이터 설계, SQL, NoSQL, 실전 DB 모델링까지 한 번에 배우는 1:1 멘토링 과정."
        path="/database"
        ogImage="/assets/images/course-thumb-database.png"
        ogType="article"
        jsonLd={courseJsonLd}
      />
      <CourseHeader {...headerData} />
      <CourseAbout {...aboutData} />
      <CourseCurriculum {...curriculumData} />
      <CourseProjects {...projectsData} courseSlug="database" />
      <CourseInstructors {...instructorsData} />
      <CourseReviewSection courseTitle="데이터베이스 모델링" />
      <CourseCta {...ctaData} />
    </>
  );
};

export default DatabasePage;
